import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, FileText, ArrowRight, Check, X, AlertCircle,
  Download, ChevronDown, Loader2, Table2, History
} from 'lucide-react';
import { Button } from '../components/ui/button';

const TARGET_TYPES = [
  { value: 'party', label: 'Parties (Clients/Vendors)', fields: ['name', 'type', 'gstin', 'address', 'mobile', 'email'] },
  { value: 'journal_entry', label: 'Journal Entries (Staging)', fields: ['date', 'voucher_type', 'narration', 'account_code', 'debit', 'credit', 'party_name'] },
];

export default function ImportEngine() {
  const { user } = useAuth();
  const [step, setStep] = useState('upload'); // upload | mapping | preview | result
  const [uploading, setUploading] = useState(false);
  const [importData, setImportData] = useState(null);
  const [targetType, setTargetType] = useState('party');
  const [fieldMapping, setFieldMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);

  useEffect(() => {
    api.get('/import/history').then(r => setImportHistory(r.data.imports || [])).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await api.post('/import/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportData(res.data);
      // Auto-map matching headers
      const target = TARGET_TYPES.find(t => t.value === targetType);
      const autoMap = {};
      if (target) {
        target.fields.forEach(f => {
          const match = res.data.headers.find(h => h.toLowerCase().replace(/[^a-z]/g, '') === f.replace(/_/g, ''));
          if (match) autoMap[f] = match;
        });
      }
      setFieldMapping(autoMap);
      setStep('mapping');
      toast.success(`Uploaded: ${res.data.row_count} rows found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const executeImport = async () => {
    if (!importData) return;
    setImporting(true);
    try {
      const res = await api.post(`/import/${importData.import_id}/execute`, {
        target_type: targetType, field_mapping: fieldMapping,
      });
      setResult(res.data);
      setStep('result');
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  const reset = () => {
    setStep('upload'); setImportData(null); setFieldMapping({}); setResult(null);
    api.get('/import/history').then(r => setImportHistory(r.data.imports || [])).catch(() => {});
  };

  const targetFields = TARGET_TYPES.find(t => t.value === targetType)?.fields || [];

  return (
    <div className="space-y-4" data-testid="import-engine-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Data Import Engine</h1>
          <p className="text-sm text-slate-500">Import data from CSV, Excel, or PDF files</p>
        </div>
        {step !== 'upload' && (
          <Button variant="outline" size="sm" onClick={reset} data-testid="import-reset">
            <X size={14} className="mr-1" /> Start Over
          </Button>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 bg-white border rounded-lg px-4 py-3">
        {['Upload File', 'Map Fields', 'Preview & Import', 'Results'].map((s, i) => {
          const stepKeys = ['upload', 'mapping', 'preview', 'result'];
          const active = stepKeys.indexOf(step) >= i;
          return (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${active ? 'text-orange-600' : 'text-slate-400'}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${active ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {stepKeys.indexOf(step) > i ? <Check size={10} /> : i + 1}
                </div>
                {s}
              </div>
              {i < 3 && <ArrowRight size={14} className="text-slate-300" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 bg-white border rounded-lg p-6">
            <div className="mb-4">
              <label className="form-label">Import Type</label>
              <select value={targetType} onChange={e => setTargetType(e.target.value)} className="form-input" data-testid="import-type">
                {TARGET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <label className="block border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors"
              data-testid="import-dropzone">
              <input type="file" accept=".csv,.xlsx,.xls,.pdf,.txt" onChange={handleUpload} className="hidden" data-testid="import-file-input" />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 size={32} className="animate-spin text-orange-600" />
                  <p className="text-sm text-slate-600">Processing file...</p>
                </div>
              ) : (
                <>
                  <Upload size={32} className="mx-auto mb-3 text-slate-400" />
                  <p className="text-sm font-medium text-slate-700 mb-1">Drop or click to upload</p>
                  <p className="text-xs text-slate-500">Supports CSV, Excel (.xlsx), and PDF files (max 20MB)</p>
                </>
              )}
            </label>
          </div>
          <div className="space-y-3">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <History size={13} /> Import History
              </h3>
              {importHistory.length === 0 ? (
                <p className="text-xs text-slate-400">No imports yet</p>
              ) : (
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {importHistory.map((imp, i) => (
                    <div key={i} className="px-2 py-1.5 bg-slate-50 rounded text-[11px]">
                      <p className="font-medium text-slate-700 truncate">{imp.filename}</p>
                      <p className="text-slate-400">{imp.row_count} rows | {imp.status} | {(imp.created_at || '').slice(0, 10)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-800 mb-1">Tips</p>
              <ul className="text-[10px] text-blue-700 space-y-0.5 leading-relaxed">
                <li>- CSV: First row must be headers</li>
                <li>- Excel: Data from the active sheet</li>
                <li>- PDF: AI extracts tabular data (Gemini)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Field Mapping */}
      {step === 'mapping' && importData && (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Map Source Fields to Target</h3>
          <div className="grid grid-cols-2 gap-4 mb-6">
            {targetFields.map(field => (
              <div key={field} className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-600 w-28 shrink-0 capitalize">{field.replace(/_/g, ' ')}</span>
                <ArrowRight size={14} className="text-slate-400 shrink-0" />
                <select value={fieldMapping[field] || ''} onChange={e => setFieldMapping({ ...fieldMapping, [field]: e.target.value })}
                  className="form-input text-xs flex-1" data-testid={`mapping-${field}`}>
                  <option value="">-- Skip --</option>
                  {importData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>

          {/* Preview Table */}
          <h4 className="text-xs font-semibold text-slate-600 mb-2">Data Preview (first 10 rows)</h4>
          <div className="overflow-x-auto border rounded-md max-h-60">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50">{importData.headers.map(h => <th key={h} className="px-2 py-1.5 text-left text-slate-500 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {importData.preview.slice(0, 10).map((row, i) => (
                  <tr key={i} className="border-t">
                    {importData.headers.map(h => <td key={h} className="px-2 py-1 text-slate-700 max-w-[150px] truncate">{row[h] || ''}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between mt-4">
            <Button variant="outline" size="sm" onClick={() => setStep('upload')}>Back</Button>
            <Button size="sm" onClick={executeImport} disabled={importing} data-testid="execute-import">
              {importing ? <><Loader2 size={14} className="animate-spin mr-1" /> Importing...</> : <><Check size={14} className="mr-1" /> Import {importData.row_count} Rows</>}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 'result' && result && (
        <div className="bg-white border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${result.imported > 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              {result.imported > 0 ? <Check size={20} className="text-emerald-600" /> : <AlertCircle size={20} className="text-red-600" />}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Import Complete</h3>
              <p className="text-xs text-slate-500">{result.imported} rows imported, {result.errors?.length || 0} errors</p>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 max-h-40 overflow-y-auto">
              <p className="text-xs font-medium text-red-800 mb-1">Errors:</p>
              {result.errors.slice(0, 10).map((err, i) => (
                <p key={i} className="text-[11px] text-red-700">{err}</p>
              ))}
            </div>
          )}
          <Button onClick={reset} data-testid="import-done">Import More Data</Button>
        </div>
      )}
    </div>
  );
}
